import { Controller } from "@hotwired/stimulus"

// Resolve-panel playlist URL paste (register 2026-08-05, item 5). Small on
// purpose: it reuses the server-side PlaylistResolver endpoint the Record
// form uses, and none of the payment-form line machinery. A cross-curator
// result is REFUSED here with a plain message: the full Record form owns
// the confirm flow (which leaves the playlist on file where it is,
// Cross_Curator_Placement_Plan.md), and accept_pitch's dropdown stays
// curator-scoped, so this panel must never tie another curator's playlist.
export default class extends Controller {
  static targets = ["url", "select", "status", "avoid"]
  static values = { resolveUrl: String }

  resolve() {
    this.doResolve()
  }

  // Paste fires before the input value updates; defer one tick.
  resolvePaste() {
    setTimeout(() => this.doResolve(), 0)
  }

  doResolve() {
    const url = this.urlTarget.value.trim()
    if (!url) return
    this.showStatus("Checking that link...", "")
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.resolveUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token, Accept: "application/json" },
      body: JSON.stringify({ url })
    })
      .then((r) => r.json())
      .then((data) => this.handleResult(data))
      .catch(() => this.showStatus("Could not check that link. Try again.", "error"))
  }

  handleResult(data) {
    switch (data.status) {
      case "resolved": {
        let option = Array.from(this.selectTarget.options).find((o) => o.value === String(data.id))
        if (!option) {
          option = new Option(data.avoid ? `${data.name} (avoid)` : data.name, data.id)
          if (data.avoid) option.dataset.avoid = data.avoid.reason
          this.selectTarget.add(option)
        }
        this.selectTarget.value = String(data.id)
        this.showAvoid()
        this.showAbove()
        const followers = data.followers != null ? ` (${Number(data.followers).toLocaleString()} followers)` : ""
        this.showStatus(`Linked: ${data.name}${followers}`, "ok")
        break
      }
      case "cross_curator":
        this.selectTarget.value = ""
        this.showAbove()
        this.showStatus(
          `That playlist is on file under ${data.other_curator_name || "a different curator"}. To use it, click Edit on this row, choose Accepted, and paste the link there.`,
          "error"
        )
        break
      case "invalid_url":
        this.showStatus(
          data.link_type ? `That is a ${data.link_type} link, not a playlist link.` : "That does not look like a Spotify playlist link.",
          "error"
        )
        break
      default:
        this.showStatus("Spotify could not find that playlist. Check the link.", "error")
    }
  }

  // Follower watch (2026-10-08): the chosen playlist's avoid reason, in red,
  // under the select. Shown for the dropdown and for a pasted link alike.
  showAvoid() {
    if (!this.hasAvoidTarget) return
    const reason = this.selectTarget.selectedOptions[0]?.dataset.avoid
    this.avoidTarget.textContent = reason ? `Avoid this playlist. ${reason}` : ""
    this.avoidTarget.style.display = reason ? "block" : "none"
  }

  // Add it above the older songs (SPT_Planner_Above_Plan.md): the server
  // draws one hidden box per playlist still holding TREND songs past their
  // month; the chosen playlist's box shows, every other one hides.
  showAbove() {
    const chosen = this.selectTarget.value
    this.element.querySelectorAll("[data-above-for]").forEach((box) => { box.hidden = box.dataset.aboveFor !== chosen })
  }

  showStatus(message, kind) {
    this.statusTarget.textContent = message
    this.statusTarget.style.display = ""
    this.statusTarget.style.color = kind === "error" ? "var(--spt-danger, #b91c1c)" : "var(--spt-text-secondary, #6b7280)"
    if (kind === "ok") this.statusTarget.style.color = "var(--spt-success, #15803d)"
  }
}
